import { useEffect, useState } from "react";
import { LogOut, KeyRound } from "lucide-react";
import { api, usePortalAuth, extractErr } from "@/portal/PortalAuth";

const btn = "rounded-full bg-[#1C1917] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#44403C] transition-colors disabled:opacity-60";
const ghost = "rounded-full border border-[#E7E5E4] text-[#1C1917] px-5 py-2.5 text-sm font-medium hover:bg-[#F5F5F4] transition-colors";
const card = "rounded-2xl border border-[#E7E5E4] bg-white p-6";
const inp = "w-full rounded-lg bg-white border border-[#E7E5E4] focus:outline-none focus:ring-1 focus:ring-[#8A9A86] focus:border-[#8A9A86] h-11 px-3 text-[#1C1917] text-sm";

export function PortalHeader({ title, subtitle }) {
  const { user, logout } = usePortalAuth();
  return (
    <div className="flex items-start justify-between gap-4 mb-8">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase text-[#8A9A86] font-medium">{subtitle}</p>
        <h1 className="mt-2 text-3xl sm:text-4xl tracking-tight font-light text-[#1C1917]">{title}</h1>
        {user && <p className="mt-2 text-sm text-[#78716C]">Signed in as {user.name} · {user.email} · <span className="text-[#8A9A86] uppercase tracking-wider">{user.role}</span></p>}
      </div>
      <button onClick={logout} data-testid="portal-logout" className={`${ghost} inline-flex items-center gap-2`}>
        <LogOut className="w-4 h-4" strokeWidth={1.5} /> Log out
      </button>
    </div>
  );
}

export function ChangePasswordGate({ children }) {
  const { user, changePassword } = usePortalAuth();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user?.must_change_password) return children;
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (pw.length < 8) throw new Error("New password must be at least 8 characters.");
      await changePassword(cur, pw);
    } catch (e) { setErr(extractErr(e)); } finally { setBusy(false); }
  };
  return (
    <section data-testid="change-password-gate" className="px-6 md:px-12 pt-36 md:pt-40 pb-24 min-h-[80vh]">
      <div className="max-w-md mx-auto rounded-2xl border border-[#E7E5E4] bg-[#F5F5F4] p-8 md:p-10">
        <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-medium text-[#8A9A86]"><KeyRound className="w-3.5 h-3.5" /> First login</span>
        <h1 className="mt-3 text-2xl font-normal tracking-tight text-[#1C1917]">Set a new password</h1>
        <p className="mt-2 text-sm text-[#44403C] font-light">For security, please change your temporary password before continuing.</p>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="change-password-form">
          <div><label className="text-sm text-[#1C1917] mb-1 block">Current password</label>
            <input type="password" required value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cp-current" className={inp} /></div>
          <div><label className="text-sm text-[#1C1917] mb-1 block">New password (min 8 chars)</label>
            <input type="password" required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="cp-new" className={inp} /></div>
          {err && <p className="text-sm text-red-600" data-testid="cp-error">{err}</p>}
          <button type="submit" disabled={busy} data-testid="cp-submit" className={`w-full ${btn} h-12`}>{busy ? "Saving…" : "Update password"}</button>
        </form>
      </div>
    </section>
  );
}

export { btn, ghost, card, inp };
